import * as React from 'react';
import { hasValue } from '../../lib/utils';
import { StyledInput } from './StyledElements';

const maxLabelLengthForErrorMessage = 30;

const Input = (props: InputProps) => {
	const { onValueChange, validate, validateOnMount = true, pattern, patternErrorMessage, ...rest } = props;
	if (patternErrorMessage && !pattern) {
		throw new Error('patternErrorMessage can not be defined without pattern being defined.');
	}
	const regex = pattern === undefined ? null : new RegExp(pattern);

	React.useEffect(() => {
		if (validateOnMount) {
			// In order to allow a parent to know whether the initial state of an input is valid or not, we fire the
			// onValueChange code.
			const value = props.checked !== undefined ? props.checked : (props.value as string | number);
			onValueChange && onValueChange(props.name, value, performAllValidation(value));
		}
	}, []);

	const className =
		(props.type === 'checkbox' ? 'k-checkbox' : 'k-textbox') + (hasValue(props.className) ? ` ${props.className}` : '');

	const performAllValidation = (value: string | boolean | number) => {
		if (props.required && (!hasValue(value) || value === false)) {
			let label = props.label || props.name;
			if (label.slice(0, 4).includes(`${props.id}.`)) label = label.replace(`${props.id}.`, '');
			// TODO Remove repetition with TextAreaField and MultipleChoiceField.
			if (label.length > maxLabelLengthForErrorMessage || label.includes('?') || label.includes('[')) label = 'This';
			return label === 'Street Name'
				? `No Street Name? Put other addressing details here instead`
				: `${label} is required.`;
		}
		if (hasValue(value) && regex instanceof RegExp && !regex.test(String(value))) {
			// Should test regex pattern only when value is not empty. https://html.spec.whatwg.org/#the-pattern-attribute
			return patternErrorMessage || 'Please enter the correct format.';
		}
		if (validate) {
			return validate(value);
		}
		return null;
	};

	const handleEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { target } = event;
		let value = target.type === 'checkbox' ? target.checked : target.value;
		if (event.type === 'change') onValueChange && onValueChange(props.name, value, performAllValidation(value));
		else if (event.type === 'blur' && typeof value === 'string') {
			value = value.trim();
			onValueChange && onValueChange(props.name, value, performAllValidation(value));
		}
	};

	// Shared with ValidationConstants.cs
	const defaultMaxLength = 256;

	return (
		<StyledInput
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...rest}
			id={props.id || props.name}
			className={className}
			value={props.value === null || props.value === undefined ? '' : props.value}
			checked={props.type === 'checkbox' ? props.checked : undefined}
			onChange={handleEvent}
			onBlur={handleEvent}
			maxLength={hasValue(props.maxLength) ? props.maxLength : defaultMaxLength}
		/>
	);
};

export default Input;

export interface ChangeableValue {
	onValueChange?: (name: string, value: string | boolean | number, errorMessage: string) => void;
}

export interface InputProps extends ChangeableValue, React.InputHTMLAttributes<HTMLInputElement> {
	label?: string;
	validate?: (value: string | boolean | number) => string;
	validateOnMount?: boolean;
	patternErrorMessage?: string;
	textEffect?: string;
}

Input.defaultProps = {
	label: null,
	validate: null,
	validateOnMount: false,
	patternErrorMessage: null,
	onValueChange: null,
	textEffect: null
};
